import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { SectionLabel } from "components/AppShell";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import { Button } from "components/ui/button";

import { useEventDeletion } from "./use-event-deletion";

/** Offered once the deletion has started, next to Close. */
export type LeaveAfterDeletion = { label: string; onLeave: () => void };

export function EventDeletionControl({
  eventId,
  eventTitle,
  uid,
  onSucceeded,
  leave,
}: {
  eventId: string;
  eventTitle: string;
  uid: string;
  onSucceeded?: () => void;
  leave?: LeaveAfterDeletion;
}) {
  const command = useEventDeletion();
  const [open, setOpen] = useState(false);
  const inFlight =
    command.state.status === "submitting" ||
    command.state.status === "watching";
  // Accepted means locked, unpublished and handed to the job, which can take
  // minutes. Nothing the admin does changes the outcome, so the dialog lets
  // go of them here rather than at the job's end.
  const started =
    (command.state.status === "watching" && command.state.accepted) ||
    command.state.status === "succeeded";
  const busy = inFlight && !started;

  // Once per success. Keyed on the status alone, with the callback read
  // through a ref: every caller passes an inline arrow, so a dependency on the
  // callback re-ran this on each render while the status stayed "succeeded",
  // and a callback that re-renders its parent looped.
  const onSucceededRef = useRef(onSucceeded);
  useEffect(() => {
    onSucceededRef.current = onSucceeded;
  });
  useEffect(() => {
    if (command.state.status === "succeeded") onSucceededRef.current?.();
  }, [command.state.status]);

  return (
    <div className="space-y-3">
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!busy) setOpen(next);
        }}
      >
        <DialogTrigger asChild>
          <Button
            variant="destructive"
            disabled={inFlight}
            data-testid={`delete-event-${eventId}`}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete event
          </Button>
        </DialogTrigger>
        <DialogContent>
          {started ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {command.state.status === "succeeded"
                    ? "Event deleted"
                    : "Deletion started"}
                </DialogTitle>
                <DialogDescription data-testid="event-deletion-started">
                  {command.state.status === "succeeded"
                    ? "The event and all its data have been removed."
                    : "The event is locked and unpublished. It will be removed shortly."}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  data-testid="close-delete-event"
                  onClick={() => setOpen(false)}
                >
                  Close
                </Button>
                {leave && (
                  <Button
                    variant="cta"
                    data-testid="leave-deleted-event"
                    onClick={() => {
                      setOpen(false);
                      leave.onLeave();
                    }}
                  >
                    {leave.label}
                  </Button>
                )}
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Delete {eventTitle}?</DialogTitle>
                <DialogDescription>
                  This immediately unpublishes and locks the event. Its
                  attendees, agenda, files, bookmarks, and all other event data
                  are then permanently deleted. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={busy}
                  data-testid="confirm-delete-event"
                  onClick={() => {
                    void command.submit(eventId, uid);
                  }}
                >
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {busy ? "Deleting…" : "Permanently delete"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {command.state.status === "watching" && (
        <p className="text-sm text-muted-foreground" role="status">
          {command.state.accepted
            ? "Deletion started. The event is locked and unpublished, and will be removed shortly."
            : "Locking event and scheduling deletion…"}
        </p>
      )}
      {command.state.status === "succeeded" && (
        <p className="text-sm text-foreground" role="status">
          Event deleted.
        </p>
      )}
      {command.state.status === "failed" && (
        <div
          className="flex items-start gap-2 text-sm text-destructive-text"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{command.state.error}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-0"
            onClick={command.reset}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

export function EventDeletionDangerZone({
  eventId,
  eventTitle,
  uid,
  onSucceeded,
  leave,
}: {
  eventId: string;
  eventTitle: string;
  uid: string;
  onSucceeded?: () => void;
  leave?: LeaveAfterDeletion;
}) {
  return (
    <Card className="border-destructive/40">
      <CardContent className="space-y-3 py-5">
        <SectionLabel>Danger zone</SectionLabel>
        <p className="text-sm text-muted-foreground">
          Permanently remove this event and all data it owns.
        </p>
        <EventDeletionControl
          eventId={eventId}
          eventTitle={eventTitle}
          uid={uid}
          onSucceeded={onSucceeded}
          leave={leave}
        />
      </CardContent>
    </Card>
  );
}
